import axios from "axios";
import Jsona from "jsona";
import Vue from "vue";
import contentDisposition from "content-disposition";

const state = {
  apiUrl: "",
  loading: false,
  processes: [],
  changeLogEntries: [],
  error: 0,
  remindChangelogForProcessIds: new Array()
};

const dataFormatter = new Jsona();

const client = axios.create();

client.interceptors.request.use(function(config) {
  var token = window.localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = "Bearer " + token;
  }

  return config;
});

client.interceptors.request.use(function(config) {
  state.loading = true;
  return config;
});
client.interceptors.response.use(
  function(response) {
    state.loading = false;
    state.error = 0;
    return response;
  },
  function(error) {
    if (
      error.request &&
      error.request.responseURL &&
      error.request.responseURL.search(new RegExp("/api/Processes/", "i")) >=
        0 &&
      error.response &&
      (error.response.status === 404 || error.response.status === 400)
    ) {
      state.error = error.response.status;
    } else {
      state.error = 0;
    }
    state.loading = false;
  }
);

const getters = {
  loading: state => {
    return state.loading;
  },
  processes: state => {
    return state.processes.sort((a, b) =>
      a.name > b.name ? 1 : b.name > a.name ? -1 : 0
    );
  },
  originalProcessIds: state => {
    let originalProcessIds = [];
    state.processes.map(process => {
      if (process.originalProcess) {
        originalProcessIds.push(process.originalProcess);
      }
    });
    return originalProcessIds;
  },
  process: state => (id = "0") => {
    let process = state.processes.find(process => process.id === id);
    return process;
  },
  processName: state => (id = "0") => {
    let process = state.processes.find(process => process.id === id);
    return process.name;
  },
  originalProcess: (state, getters) => (id = "0") => {
    let process = getters.process(id);
    let originalProcess = {};
    if (process && process.originalProcess) {
      originalProcess = getters.process(process.originalProcess.toLowerCase()); // TODO lowercase?
    }
    return originalProcess;
  },
  processSteps: (state, getters) => (processId = "0") => {
    let processSteps = [];
    let process = getters.process(processId);
    if (process && process.steps && process.steps.length > 0) {
      let sortedSteps = [...process.steps].sort((a, b) =>
        a.sortIndex > b.sortIndex ? 1 : b.sortIndex > a.sortIndex ? -1 : 0
      );
      processSteps = sortedSteps;
      let publishedIndex = 0;
      processSteps.map(step => {
        if (step.status === "Published") {
          publishedIndex++;
          step.number = publishedIndex;
        } else {
          let noIndex = "-";
          step.number = noIndex;
        }
      });
      return processSteps;
    }
  },
  resources: (state, getters) => (processId = "0", processStepId = "0") => {
    let resources = [];
    let process = getters.process(processId);
    if (process && process.steps) {
      let processStep = process.steps.find(step => step.id === processStepId);
      if (processStep) {
        let sortedResources = [...processStep.resources].sort((a, b) =>
          a.sortIndex > b.sortIndex ? 1 : b.sortIndex > a.sortIndex ? -1 : 0
        );
        resources = sortedResources;
        return resources;
      }
    }
    return [];
  },
  resourceLinks: (state, getters) => (
    processId = "0",
    processStepId = "0",
    resourceId = "0"
  ) => {
    let links = [];
    let process = getters.process(processId);
    if (process && process.steps) {
      let processStep = process.steps.find(step => step.id === processStepId);
      if (processStep && processStep.resources) {
        let resource = processStep.resources.find(
          resource => resource.id === resourceId
        );
        if (resource && resource.links && resource.links.length > 0) {
          let sortedLinks = [...resource.links].sort((a, b) =>
            a.attributes.sortIndex > b.attributes.sortIndex
              ? 1
              : b.attributes.sortIndex > a.attributes.sortIndex
                ? -1
                : 0
          );
          links = sortedLinks;
          return links;
        }
      }
    }
    return [];
  },
  changeLogEntries: state => {
    return state.changeLogEntries.sort((a, b) =>
      a.date < b.date ? 1 : b.date < a.date ? -1 : 0
    );
  },
  error: state => {
    return state.error;
  },
  remindChangelogForProcessIds: state => {
    return state.remindChangelogForProcessIds;
  }
};

const actions = {
  setAppData({ commit }, { apiUrl }) {
    commit("setAppData", { apiUrl });
  },
  exportPdfs({ state }) {
    return new Promise(resolve => {
      client
        .get(state.apiUrl + "/api/Processes/ExportPdfs", {
          responseType: "arraybuffer",
          headers: {
            "Content-Type": "application/json"
          }
        })
        .then(response => {
          if (response && response.data) {
            const disposition = response.request.getResponseHeader(
              "Content-Disposition"
            );
            const parsedDisponition = contentDisposition.parse(disposition);
            const filename = parsedDisponition.parameters.filename;

            let isIE = /* @cc_on!@ */ false || !!document.documentMode;
            let file = new Blob([response.data], {
              type: response.headers["content-type"]
            });
            if (isIE) {
              window.navigator.msSaveOrOpenBlob(file, filename);
            } else {
              const fileURL = URL.createObjectURL(file);
              const link = document.createElement("a");
              link.href = fileURL;
              link.download = filename;
              link.click();
            }
            resolve();
          }
        });
    });
  },
  fetchProcesses({ state, commit, getters }) {
    if (getters.processes.length > 2) {
      return;
    }
    return new Promise(resolve => {
      client.get(state.apiUrl + "/api/Processes").then(response => {
        if (response && response.data) {
          let formattedResponse = dataFormatter.deserialize(response.data);
          commit("setProcesses", formattedResponse);
          resolve();
        }
      });
    });
  },
  fetchProcess({ state, commit, dispatch, getters }, { id, hasAccess }) {
    let process = getters.process(id);
    if (process) {
      return;
    }
    return new Promise(resolve => {
      client.get(state.apiUrl + "/api/Processes/" + id).then(response => {
        if (response && response.data) {
          let formattedResponse = dataFormatter.deserialize(response.data);
          commit("addProcess", formattedResponse);
          if (formattedResponse.originalProcess && hasAccess) {
            dispatch("fetchProcess", {
              id: formattedResponse.originalProcess,
              apiUrl: state.apiUrl
            });
          }
          resolve();
        }
      });
    });
  },
  fetchChangeLog({ state, commit }, { id }) {
    return new Promise(resolve => {
      client
        .get(state.apiUrl + "/api/Processes/" + id + "/Changelog/")
        .then(response => {
          if (response && response.data) {
            let formattedResponse = dataFormatter.deserialize(response.data);
            commit("setChangeLog", formattedResponse);
            resolve();
          }
        });
    });
  },
  createProcess(
    { state, commit },
    { name, description, category, visibility }
  ) {
    return new Promise(resolve => {
      let newProcess = {
        name: name,
        category: category,
        description: description,
        visibility: visibility
      };
      client
        .post(state.apiUrl + "/api/Processes", newProcess)
        .then(response => {
          if (response && response.data) {
            let formattedResponse = dataFormatter.deserialize(response.data);
            commit("addProcess", formattedResponse);
            commit("addProcessIdToRemindChangelog", response.data.data.id);
            resolve(response.data.data.id);
          }
        });
    });
  },
  editProcess({ state, commit }, { id, name, description }) {
    return new Promise(resolve => {
      client
        .patch(state.apiUrl + "/api/Processes/" + id, {
          name: name,
          description: description
        })
        .then(response => {
          if (response && response.data) {
            commit("updateProcessInfo", {
              id: id,
              updated: dataFormatter.deserialize(response.data)
            });
            commit("addProcessIdToRemindChangelog", id);
            resolve();
          }
        });
    });
  },
  deleteProcess({ state, commit }, { id }) {
    return new Promise(resolve => {
      client.delete(state.apiUrl + "/api/Processes/" + id).then(response => {
        if (response && response.status === 200) {
          commit("deleteProcess", id);
          resolve();
        }
      });
    });
  },
  createProcessStep({ state, commit }, { processId, name, description }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessSteps/", {
          processId,
          name,
          description
        })
        .then(response => {
          if (response && response.data) {
            commit("addProcessStep", {
              processId: processId,
              processStep: dataFormatter.deserialize(response.data)
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(response.data.data.id);
          }
        });
    });
  },
  deleteProcessStep({ state, commit }, { processId, id }) {
    return new Promise(resolve => {
      client.delete(state.apiUrl + "/api/ProcessSteps/" + id).then(response => {
        if (response && response.status === 200) {
          commit("deleteProcessStep", { processId, id });
          commit("addProcessIdToRemindChangelog", processId);
          resolve(id);
        }
      });
    });
  },
  editProcessStep({ state, commit }, { id, processId, name, description }) {
    return new Promise(resolve => {
      client
        .patch(state.apiUrl + "/api/ProcessSteps/" + id, { name, description })
        .then(response => {
          if (response && response.data) {
            commit("updateProcessStepInfo", {
              processId: processId,
              updated: dataFormatter.deserialize(response.data)
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(response.data.data.id);
          }
        });
    });
  },
  publishProcessStep({ state, commit }, { processId, id }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessSteps/" + id + "/Publish")
        .then(response => {
          if (response) {
            commit("publishProcessStep", {
              processId: processId,
              id: id
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve();
          }
        });
    });
  },
  sortProcessStep({ state, commit }, { processId, id, sortIndex }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessSteps/" + id + "/Sort", {
          sortIndex: sortIndex
        })
        .then(response => {
          if (response && response.data && response.data.succeeded) {
            commit("sortProcessStep", {
              processId: processId,
              id: id,
              sortIndex: sortIndex
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve();
          }
        });
    });
  },
  unpublishProcessStep({ state, commit }, { processId, id }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessSteps/" + id + "/Unpublish")
        .then(response => {
          if (response) {
            commit("unpublishProcessStep", {
              processId: processId,
              id: id
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve();
          }
        });
    });
  },
  createProcessStepResource({ state, commit }, { processId, resource }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessStepResources", {
          processStepId: resource.processStepId,
          description: resource.description,
          responsible: resource.responsible,
          archive: resource.archive,
          archivePeriod: resource.archivePeriod
        })
        .then(response => {
          if (response && response.data) {
            commit("addResource", {
              processId: processId,
              stepId: resource.processStepId,
              resource: dataFormatter.deserialize(response.data)
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(response.data.data.id);
          }
        });
    });
  },
  editProcessStepResource(
    { state, commit },
    {
      processId,
      processStepId,
      resourceId,
      description,
      responsible,
      archive,
      archivePeriod
    }
  ) {
    return new Promise(resolve => {
      client
        .patch(state.apiUrl + "/api/ProcessStepResources/" + resourceId, {
          description,
          responsible,
          archive,
          archivePeriod
        })
        .then(response => {
          if (response && response.data) {
            commit("updateResourceInfo", {
              processId: processId,
              processStepId: processStepId,
              updated: dataFormatter.deserialize(response.data)
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(response.data.data.id);
          }
        });
    });
  },
  deleteProcessStepResource(
    { state, commit },
    { processId, processStepId, resourceId }
  ) {
    return new Promise(resolve => {
      client
        .delete(state.apiUrl + "/api/ProcessStepResources/" + resourceId)
        .then(response => {
          if (response && response.status === 200) {
            commit("deleteResource", { processId, processStepId, resourceId });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(resourceId);
          }
        });
    });
  },
  sortProcessStepResources(
    { state, commit },
    { processId, id, processStepId, sortIndex }
  ) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessStepResources/" + id + "/Sort", {
          sortIndex: sortIndex,
          processStepId: processStepId
        })
        .then(response => {
          if (response && response.data && response.data.succeeded) {
            commit("sortResources", {
              processId: processId,
              id: id,
              processStepId: processStepId,
              sortIndex: sortIndex
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve();
          }
        });
    });
  },
  createProcessStepResourceLink(
    { state, commit },
    { processId, processStepId, processStepResourceId, text, link }
  ) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessStepResourceLinks", {
          processStepResourceId,
          text,
          link
        })
        .then(response => {
          if (response && response.data) {
            commit("addLink", {
              processId: processId,
              processStepId: processStepId,
              resourceId: processStepResourceId,
              link: response.data.data
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(response.data.data.id);
          }
        });
    });
  },
  editProcessStepResourceLink(
    { state, commit },
    { processId, processStepId, resourceId, resourceLinkId, text, link }
  ) {
    return new Promise(resolve => {
      client
        .patch(
          state.apiUrl + "/api/ProcessStepResourceLinks/" + resourceLinkId,
          { text, link }
        )
        .then(response => {
          if (response) {
            commit("editLink", {
              processId: processId,
              processStepId: processStepId,
              resourceId: resourceId,
              linkId: resourceLinkId,
              text: text,
              link: link
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(response.data.data.id);
          }
        });
    });
  },
  sortProcessStepResourceLink(
    { state, commit },
    { processId, id, processStepId, resourceId, sortIndex }
  ) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/ProcessStepResourceLinks/" + id + "/Sort", {
          sortIndex: sortIndex,
          resourceId: resourceId
        })
        .then(response => {
          if (response && response.data && response.data.succeeded) {
            commit("sortResourceLinks", {
              processId: processId,
              id: id,
              processStepId: processStepId,
              resourceId: resourceId,
              sortIndex: sortIndex
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve();
          }
        });
    });
  },
  deleteProcessStepResourceLink(
    { state, commit },
    { processId, processStepId, resourceId, resourceLinkId }
  ) {
    return new Promise(resolve => {
      client
        .delete(
          state.apiUrl + "/api/ProcessStepResourceLinks/" + resourceLinkId
        )
        .then(response => {
          if (response && response.status === 200) {
            commit("deleteLink", {
              processId,
              processStepId,
              resourceId,
              resourceLinkId
            });
            commit("addProcessIdToRemindChangelog", processId);
            resolve(resourceLinkId);
          }
        });
    });
  },
  publishProcess({ state, commit }, { id }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/Processes/" + id + "/Publish")
        .then(response => {
          if (response) {
            commit("publishProcess", id);
            resolve();
          }
        });
    });
  },
  unpublishProcess({ state, commit }, { id }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/Processes/" + id + "/Unpublish")
        .then(response => {
          if (response) {
            commit("unpublishProcess", id);
            resolve();
          }
        });
    });
  },
  customizeProcess({ state }, { id }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/Processes/" + id + "/Customize")
        .then(response => {
          if (response && response.data) {
            resolve(response.data.data.id);
          }
        });
    });
  },
  createChangeLogEntity({ state, commit }, { message, id, date, notify }) {
    return new Promise(resolve => {
      let newChangeLogEntity = {
        message: message,
        date: date,
        notify: notify
      };
      client
        .post(
          state.apiUrl + "/api/Processes/" + id + "/ChangeLog",
          newChangeLogEntity
        )
        .then(response => {
          if (response && response.data) {
            commit(
              "addChangeLogEntity",
              dataFormatter.deserialize(response.data)
            );
            resolve(response.data.data.id);
          }
        });
    });
  },
  markAsRead({ state, commit }, { id }) {
    return new Promise(resolve => {
      client
        .post(state.apiUrl + "/api/Processes/" + id + "/Changelog/MarkAsRead")
        .then(response => {
          if (response && response.data && response.data.succeeded) {
            commit("markAsRead", id);
            resolve();
          }
        });
    });
  },
  removeProcessIdRemindChangelog({ commit }, { id }) {
    commit("removeProcessIdFromRemindChangelog", id);
  }
};

const mutations = {
  setAppData(state, { apiUrl }) {
    state.apiUrl = apiUrl;
  },
  setProcesses(state, processes) {
    processes.map(process => {
      if (process.visibility === "Public") {
        process.processType = "ProcessTemplate";
      } else {
        process.processType = "Process";
      }
    });
    Vue.set(state, "processes", processes);
  },
  addProcess(state, process) {
    if (process.visibility === "Public") {
      process.processType = "ProcessTemplate";
    } else {
      process.processType = "Process";
    }
    state.processes.push(process);
  },
  setChangeLog(state, changeLogEntries) {
    Vue.set(state, "changeLogEntries", changeLogEntries);
  },
  updateProcessInfo(state, { id, updated }) {
    let process = state.processes.find(process => process.id === id);
    if (process) {
      process.name = updated.name;
      process.description = updated.description;
    }
  },
  deleteProcess(state, id) {
    Vue.delete(
      state.processes,
      state.processes.findIndex(process => process.id === id)
    );
  },
  addChangeLogEntity(state, changeLogEntity) {
    state.changeLogEntries.unshift(changeLogEntity);
  },
  addProcessStep(state, { processId, processStep }) {
    let process = state.processes.find(process => process.id === processId);
    if (process) {
      process.steps.push(processStep);
    }
  },
  updateProcessStepInfo(state, { processId, updated }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === updated.id);
    if (!step) return;
    step.name = updated.name;
    step.description = updated.description;
  },
  deleteProcessStep(state, { processId, id }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    Vue.delete(
      process.steps,
      process.steps.findIndex(step => step.id === id)
    );
  },
  sortProcessStep(state, { processId, id, sortIndex }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let stepToMove = process.steps.find(step => step.id === id);
    let currentlyAtIndex = process.steps.filter(
      step => step.sortIndex === sortIndex
    );

    for (let step of currentlyAtIndex) {
      // Just in case there are more than one
      if (stepToMove.sortIndex < sortIndex) {
        step.sortIndex = step.sortIndex - 1;
      } else {
        step.sortIndex = step.sortIndex + 1;
      }
    }
    stepToMove.sortIndex = sortIndex;
  },
  sortResourceLinks(
    state,
    { processId, id, processStepId, resourceId, sortIndex }
  ) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === processStepId);
    if (!step) return;
    let resource = step.resources.find(resource => resource.id === resourceId);
    if (!resource) return;

    if (resource.links.map(x => x.attributes.sortIndex == 0).length > 1) {
      resource.links.map((l, i) => (l.attributes.sortIndex = i));
    }
    let stepresourceLinkToMove = resource.links.find(link => link.id === id);
    let currentlyAtIndex = resource.links.filter(
      link => link.attributes.sortIndex === sortIndex
    );
    for (let link of currentlyAtIndex) {
      // Just in case there are more than one
      if (stepresourceLinkToMove.attributes.sortIndex < sortIndex) {
        link.attributes.sortIndex = link.attributes.sortIndex - 1;
      } else {
        link.attributes.sortIndex = link.attributes.sortIndex + 1;
      }
    }
    stepresourceLinkToMove.attributes.sortIndex = sortIndex;
  },
  addResource(state, { processId, stepId, resource }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === stepId);
    if (!step) return;
    if (!step.resources) step.resources = [];
    if (step.resources.map(x => x.sortIndex == 0).length > 1) {
      step.resources.map((r, i) => (r.sortIndex = i));
    }
    step.resources.push(resource);
  },
  updateResourceInfo(state, { processId, processStepId, updated }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === processStepId);
    if (!step) return;
    let resource = step.resources.find(resource => resource.id === updated.id);
    if (!resource) return;
    resource.description = updated.description;
    resource.responsible = updated.responsible;
    resource.archive = updated.archive;
    resource.archivePeriod = updated.archivePeriod;
  },
  deleteResource(state, { processId, processStepId, resourceId }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === processStepId);
    if (!step) return;
    step.resources.splice(
      step.resources.findIndex(resource => resource.id === resourceId),
      1
    );
  },
  sortResources(state, { processId, id, processStepId, sortIndex }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === processStepId);
    if (!step) return;

    if (step.resources.map(x => x.sortIndex == 0).length > 1) {
      step.resources.map((r, i) => (r.sortIndex = i));
    }
    let stepresourceToMove = step.resources.find(
      resource => resource.id === id
    );
    let currentlyAtIndex = step.resources.filter(
      r => r.sortIndex === sortIndex
    );
    for (let resource of currentlyAtIndex) {
      // Just in case there are more than one
      if (stepresourceToMove.sortIndex < sortIndex) {
        resource.sortIndex = resource.sortIndex - 1;
      } else {
        resource.sortIndex = resource.sortIndex + 1;
      }
    }
    stepresourceToMove.sortIndex = sortIndex;
  },
  addLink(state, { processId, processStepId, resourceId, link }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === processStepId);
    if (!step) return;
    let resource = step.resources.find(resource => resource.id === resourceId);
    if (!resource) return;
    if (!resource.links) resource.links = [];
    if (resource.links.map(x => x.attributes.sortIndex == 0).length > 1) {
      resource.links.map((l, i) => (l.attributes.sortIndex = i));
    }
    resource.links.push(link);
  },
  editLink(
    state,
    { processId, processStepId, resourceId, linkId, text, link }
  ) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === processStepId);
    if (!step) return;
    let resource = step.resources.find(resource => resource.id === resourceId);
    if (!resource) return;
    let oldLink = resource.links.find(link => link.id === linkId);
    if (!oldLink) return;
    Vue.set(oldLink.attributes, "text", text);
    Vue.set(oldLink.attributes, "link", link);
  },
  deleteLink(state, { processId, processStepId, resourceId, resourceLinkId }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === processStepId);
    if (!step) return;
    let resource = step.resources.find(resource => resource.id === resourceId);
    if (!resource) return;
    Vue.delete(
      resource.links,
      resource.links.findIndex(link => link.id === resourceLinkId)
    );
  },
  publishProcess(state, id) {
    let process = state.processes.find(process => process.id === id);
    if (process) {
      process.status = "Published";
    }
  },
  unpublishProcess(state, id) {
    let process = state.processes.find(process => process.id === id);
    if (process) {
      process.status = "Unpublished";
    }
  },
  publishProcessStep(state, { processId, id }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === id);
    if (!step) return;
    step.status = "Published";
  },
  unpublishProcessStep(state, { processId, id }) {
    let process = state.processes.find(process => process.id === processId);
    if (!process) return;
    let step = process.steps.find(step => step.id === id);
    if (!step) return;
    step.status = "Unpublished";
  },
  markAsRead(state, id) {
    let process = state.processes.find(process => process.id === id);
    if (process) {
      process.notifications = [];
    }
  },
  addProcessIdToRemindChangelog(state, id) {
    if (state.remindChangelogForProcessIds.includes(id)) {
      return;
    }
    let newList = state.remindChangelogForProcessIds.filter(x => x);
    newList.push(id);
    Vue.set(state, "remindChangelogForProcessIds", newList);
  },
  removeProcessIdFromRemindChangelog(state, id) {
    if (
      state.remindChangelogForProcessIds.length <= 0 ||
      !state.remindChangelogForProcessIds.includes(id)
    ) {
      return;
    }
    let newList = state.remindChangelogForProcessIds.filter(x => x && x !== id);
    Vue.set(state, "remindChangelogForProcessIds", newList);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
